/** The quick actions every value on a delivery-log surface offers.
 *
 *  Id spaces (see gridLinks): `participant=` is the console booking pk (so
 *  `participant_id` links `/bookings/<id>`); `booking=` is the booking-system
 *  id string; `log=` is the event log id shared across redeliveries; `tag=` is
 *  the redelivery run tag (NOT a group-tour tag); `series=` the error series.
 *  Every /delivery-logs and /api-requests href carries a `created` window. */
import { act, type ValueAction } from '@/components/ValueActions/ValueActions';
import {
  bookingIdActions,
  customIdActions,
  emailActions,
  orderActions,
  refActions,
  tenantActions,
  tourActions,
  type BookingCtx,
} from '@/features/bookings/overview/bookingLinks';
import {
  apiRequestsHref,
  bookingsHref,
  cond,
  createdAroundNode,
  deliveryLogsHref,
  importRunsHref,
  notificationsHref,
  sessionsHref,
} from '@/utils/gridLinks';

export type DeliveryLogCtx = BookingCtx;

export const ctxOf = (d: { tour_operator_id: number | null }): DeliveryLogCtx => ({
  tenantId: d.tour_operator_id,
});

const tenantOf = (ctx: DeliveryLogCtx) => ctx.tenantId ?? null;

export { tenantActions, tourActions, orderActions, refActions, customIdActions };

export function eventActions(
  event: string,
  d: { user_id: number | null; participant_id: number | null },
  ctx: DeliveryLogCtx,
): ValueAction[] {
  const t = tenantOf(ctx);
  const out: ValueAction[] = [
    act.copy(event, 'Copy event code'),
    act.filter(deliveryLogsHref([cond('event', event)], { tenant: t }), 'Same event, all recipients (7 days)'),
  ];
  if (d.participant_id != null) {
    out.push(
      act.filter(
        deliveryLogsHref([cond('event', event), cond('participant', String(d.participant_id), 'exact')], { tenant: t }),
        'Same event on this booking',
      ),
    );
  }
  if (d.user_id != null) {
    out.push(
      act.filter(
        deliveryLogsHref([cond('event', event), cond('user', String(d.user_id))], { tenant: t }),
        'Same event to this recipient',
      ),
    );
  }
  out.push(act.filter(notificationsHref([cond('event', event)], t), 'Notifications for this event'));
  return out;
}

export function logIdActions(logId: string, ctx: DeliveryLogCtx): ValueAction[] {
  return [
    act.copy(logId, 'Copy log id'),
    act.filter(deliveryLogsHref([cond('log', logId)], { tenant: tenantOf(ctx) }), 'Every attempt with this log id'),
  ];
}

export function logTagActions(tag: string, ctx: DeliveryLogCtx): ValueAction[] {
  return [
    act.copy(tag, 'Copy log tag'),
    act.filter(deliveryLogsHref([cond('tag', tag)], { tenant: tenantOf(ctx) }), 'Same redelivery run tag'),
  ];
}

export function uuidActions(id: string): ValueAction[] {
  return [act.copy(id, 'Copy uuid')];
}

export function statusActions(label: string, ctx: DeliveryLogCtx): ValueAction[] {
  return [
    act.filter(deliveryLogsHref([cond('status', label)], { tenant: tenantOf(ctx) }), `"${label}" logs (7 days)`),
  ];
}

export function emittedActions(iso: string, ctx: DeliveryLogCtx): ValueAction[] {
  const t = tenantOf(ctx);
  return act.timeRanges(iso, (range) => deliveryLogsHref([range], { tenant: t }), 'created');
}

/** `finalized` ranges — the builder adds the created window the grid needs. */
export function finalizedActions(iso: string, ctx: DeliveryLogCtx): ValueAction[] {
  const t = tenantOf(ctx);
  return act.timeRanges(iso, (range) => deliveryLogsHref([range], { tenant: t }), 'finalized');
}

export function errorSeriesActions(seriesId: string, ctx: DeliveryLogCtx): ValueAction[] {
  return [
    act.copy(seriesId, 'Copy error series id'),
    act.filter(deliveryLogsHref([cond('series', seriesId)], { tenant: tenantOf(ctx) }), 'Every delivery in this error series'),
  ];
}

export function tenantDeliveryActions(tenant: { id: number }): ValueAction[] {
  return [
    ...tenantActions(tenant),
    act.filter(deliveryLogsHref([], { tenant: tenant.id }), 'Delivery logs of this operator (7 days)'),
  ];
}

export function recipientActions(
  d: { user_id: number | null; user_email: string | null },
  ctx: DeliveryLogCtx,
): ValueAction[] {
  const t = tenantOf(ctx);
  const out: ValueAction[] = [];
  if (d.user_id != null) {
    out.push(
      act.open(`/users/${d.user_id}`, 'Open user'),
      act.filter(deliveryLogsHref([cond('user', String(d.user_id))], { tenant: t }), 'Delivery logs to this user (7 days)'),
      act.filter(bookingsHref([cond('user', String(d.user_id))], t), 'Bookings of this user'),
      act.filter(sessionsHref({ user: d.user_id }), 'Sessions of this user'),
    );
  }
  if (d.user_email) out.push(...emailActions(d.user_email, ctx));
  return out;
}

/** `participant_id` IS the console booking pk. */
export function participantActions(participantId: number, ctx: DeliveryLogCtx): ValueAction[] {
  return [act.open(`/bookings/${participantId}`, 'Open booking'), ...bookingIdActions(participantId, ctx)];
}

export function departureActions(label: string, ctx: DeliveryLogCtx): ValueAction[] {
  return [
    act.filter(deliveryLogsHref([cond('departure', label)], { tenant: tenantOf(ctx) }), 'Delivery logs for this departure slot'),
  ];
}

export function requestIdActions(requestId: string, createdTs: string, ctx: DeliveryLogCtx): ValueAction[] {
  const t = tenantOf(ctx);
  return [
    act.copy(requestId, 'Copy request id'),
    act.filter(
      apiRequestsHref([cond('request', requestId, 'exact')], { tenant: t, window: createdAroundNode(createdTs) }),
      'The API request that emitted this (±1 h)',
    ),
    act.filter(deliveryLogsHref([cond('request', requestId)], { tenant: t }), 'Delivery logs from the same request'),
    act.filter(importRunsHref([cond('request', requestId, 'exact')], t), 'Import runs from the same request'),
  ];
}

export function notificationActions(
  notificationId: number,
  d: { participant_id: number | null },
  ctx: DeliveryLogCtx,
): ValueAction[] {
  const t = tenantOf(ctx);
  const nid = cond('notification', String(notificationId), 'exact');
  const out: ValueAction[] = [
    act.open(`/notifications/${notificationId}`, 'Open notification'),
    act.filter(deliveryLogsHref([nid], { tenant: t }), 'This notification, all recipients (7 days)'),
  ];
  if (d.participant_id != null) {
    out.push(
      act.filter(
        deliveryLogsHref([nid, cond('participant', String(d.participant_id), 'exact')], { tenant: t }),
        'This notification on this booking',
      ),
    );
  }
  return out;
}

/** Another delivery in the chain / recovery story. */
export function chainItemActions(id: string): ValueAction[] {
  return [act.open(`/delivery-logs/${id}`, 'Open delivery'), act.copy(id, 'Copy uuid')];
}
